/**
 * Cargar o editar una oferta del home.
 *
 * POR QUE NO SALE DE UN PRODUCTO
 * ------------------------------
 * Un combo no existe en el stock: no hay un SKU "iPhone 15 con cargador". Y un
 * precio promocional metido en el producto real se lleva puesto su margen y su
 * recalculo por dolar. Por eso la oferta es su propia fila, con su titulo y su
 * precio (migracion 0060), y en la tienda abre WhatsApp en vez de sumarse al
 * carrito.
 *
 * DOS CLASES Y NO UN TEXTO LIBRE
 * ------------------------------
 * COMBO y BAJA. Con un cartelito libre, en tres semanas hay "OFERTA", "PROMO",
 * "OFERTA!!" y "combo" en la misma fila del catalogo.
 *
 * EL EJEMPLO VIENE ESCRITO
 * -------------------------
 * Una oferta nueva arranca eligiendo el tipo, y el titulo y la segunda linea
 * ya vienen escritos para ese tipo (`ejemploDe`): se retoca en vez de tipearse.
 * Antes eran siete ofertas apagadas precargadas en la lista, que parecian
 * guardadas por el local (0105). Mientras el texto sea el del ejemplo, cambiar
 * el tipo, el equipo o el porcentaje lo reescribe; tocado, es de quien lo
 * escribio.
 *
 * EDITAR, NO BORRAR Y VOLVER A CARGAR
 * -----------------------------------
 * Antes una oferta solo se prendia, se apagaba o se borraba: cambiarle el
 * precio era tipearla de nuevo. El mismo formulario sirve para las dos cosas.
 */
import { useEffect, useMemo, useState } from "react";
import { convertir, formatear } from "@tora/shared";
import { ChevronRight, Trash2 } from "lucide-react";

import { SelectorProducto } from "@/components/catalogo/Campos";
import { etiquetaDeOferta, nombreLegible } from "@/components/tienda/logica";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Aviso } from "@/components/ui/aviso";
import { SelectNativo } from "@/components/ui/select-nativo";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { srcImagenOfertaAdmin } from "@/lib/imagenes";
import { useConfig, useProductos } from "@/lib/queries";
import type { Oferta, Producto } from "@/lib/tipos";

import { BotonFoto } from "./BotonFoto";
import {
  useActualizarOferta,
  useBorrarOferta,
  useCrearOferta,
  useQuitarImagenOferta,
  useSubirImagenOferta,
} from "./consultas";

/** El formulario en crudo: todo texto, porque eso es lo que tipea la persona. */
export interface OfertaEnEdicion {
  clase: Oferta["clase"];
  titulo: string;
  detalle: string;
  precio: string;
  moneda: Oferta["moneda"];
  precio_antes: string;
  porcentaje: string;
}

/**
 * Que pide cada tipo (0100). El formulario muestra solo esos campos: un plan
 * canje con un casillero de precio invita a inventarle uno.
 */
const TIPOS: Record<
  Oferta["clase"],
  {
    nombre: string;
    equipo: boolean;
    precio: "si" | "opcional" | "no";
    antes: boolean;
  }
> = {
  COMBO: {
    nombre: "Combo: un equipo con algo de regalo",
    equipo: true,
    precio: "si",
    antes: true,
  },
  BAJA: {
    nombre: "Bajó de precio",
    equipo: true,
    precio: "si",
    antes: true,
  },
  ULTIMAS: {
    nombre: "Últimas unidades",
    equipo: true,
    precio: "si",
    antes: false,
  },
  EFECTIVO: {
    nombre: "Descuento pagando en efectivo",
    equipo: true,
    precio: "opcional",
    antes: false,
  },
  PREVENTA: {
    nombre: "Preventa: reservá con una seña",
    equipo: true,
    precio: "opcional",
    antes: false,
  },
  CANJE: {
    nombre: "Plan canje: tu usado como parte de pago",
    equipo: false,
    precio: "no",
    antes: false,
  },
  ENVIO: {
    nombre: "Envío gratis",
    equipo: false,
    precio: "no",
    antes: false,
  },
};

/**
 * El texto con el que arranca una oferta nueva de cada tipo. Con equipo
 * elegido lo nombra; sin, habla en general.
 */
export function ejemploDe(
  clase: Oferta["clase"],
  equipo: string | null,
  porcentaje: string,
): { titulo: string; detalle: string } {
  const pct = porcentaje.trim() === "" ? "10" : porcentaje.trim();
  switch (clase) {
    case "COMBO":
      return {
        titulo: `${equipo ?? "Tu iPhone"} con funda y vidrio de regalo`,
        detalle: "Protegido desde el primer día",
      };
    case "BAJA":
      return {
        titulo: equipo ? `${equipo} bajó de precio` : "Bajaron los iPhone",
        detalle: "Solo esta semana",
      };
    case "ULTIMAS":
      return {
        titulo: equipo ? `Últimas unidades: ${equipo}` : "Últimas unidades",
        detalle: "Quedan pocas en el local",
      };
    case "EFECTIVO":
      return {
        titulo: equipo
          ? `${equipo} con ${pct} % off en efectivo`
          : `${pct} % off pagando en efectivo`,
        detalle: "O por transferencia",
      };
    case "PREVENTA":
      return {
        titulo: `Reservá el ${equipo ?? "próximo iPhone"} con una seña`,
        detalle: "Sé de los primeros en tenerlo",
      };
    case "CANJE":
      return {
        titulo: "Entregá tu usado y llevate uno nuevo",
        detalle: "Te lo tomamos como parte de pago",
      };
    case "ENVIO":
      return { titulo: "Te lo llevamos sin cargo", detalle: "Dentro de la ciudad" };
  }
}

/**
 * Reescribe el titulo y la segunda linea que sigan siendo los del ejemplo, en
 * una nueva y tambien en una que ya existe.
 *
 * Antes, en una que ya existia, no se tocaba nada: «el texto es del local». Y
 * si ese texto era el ejemplo tal cual, pasar el descuento de 10 a 15 dejaba
 * la tarjeta diciendo «AirPods 4 con 10 % off en efectivo» arriba del cartel
 * «15 % OFF», y el WhatsApp salia con el 10 y el precio del 15. Lo escrito a
 * mano sigue a salvo igual: solo se pisa lo que es identico al ejemplo viejo.
 */
/**
 * Si el precio de la oferta, llevado a la moneda del equipo, no se parece a
 * lo que el equipo vale: mas del doble o menos de la mitad.
 *
 * Elegir el equipo pone su moneda en el selector, y el numero se sigue
 * tipeando pensando en la otra: $ 250.000 en un campo en dolares publico
 * «AirPods 4 bajó de precio - $ 397.500.000» en la tienda, sin ningun aviso.
 */
export function precioDesproporcionado(
  precio: number | null,
  moneda: "ARS" | "USD",
  equipo: { precio_venta: unknown; precio_moneda: "ARS" | "USD" } | null,
  cotizacion: number,
): boolean {
  const valor = Number(equipo?.precio_venta);
  if (!equipo || precio === null || !(precio > 0) || !(valor > 0) || !(cotizacion > 0))
    return false;
  const razon = convertir(precio, moneda, equipo.precio_moneda, cotizacion) / valor;
  return razon > 2 || razon < 0.5;
}

export function conElEjemplo(
  antes: OfertaEnEdicion,
  despues: OfertaEnEdicion,
  equipoAntes: string | null,
  equipoDespues: string | null,
): OfertaEnEdicion {
  const viejo = ejemploDe(antes.clase, equipoAntes, antes.porcentaje);
  const nuevo = ejemploDe(despues.clase, equipoDespues, despues.porcentaje);
  return {
    ...despues,
    titulo: antes.titulo === viejo.titulo ? nuevo.titulo : despues.titulo,
    detalle: antes.detalle === viejo.detalle ? nuevo.detalle : despues.detalle,
  };
}

function ofertaNueva(clase: Oferta["clase"]): OfertaEnEdicion {
  const porcentaje = clase === "EFECTIVO" ? "10" : "";
  return {
    clase,
    ...ejemploDe(clase, null, porcentaje),
    precio: "",
    moneda: "ARS",
    precio_antes: "",
    porcentaje,
  };
}

function desdeOferta(o: Oferta): OfertaEnEdicion {
  return {
    clase: o.clase,
    titulo: o.titulo,
    detalle: o.detalle ?? "",
    precio: o.precio === null ? "" : String(Number(o.precio) || ""),
    moneda: o.moneda,
    precio_antes: o.precio_antes ? String(Number(o.precio_antes)) : "",
    porcentaje: o.porcentaje ? String(o.porcentaje) : "",
  };
}

export interface DialogOfertaProps {
  abierto: boolean;
  onCerrar: () => void;
  /** `null` es una oferta nueva. */
  oferta: Oferta | null;
  /** Donde va una nueva: al final, para no desplazar a la que el local puso primera. */
  ordenNueva: number;
}

/**
 * El dialogo. El formulario vive en un componente aparte que solo existe con
 * el dialogo abierto (Radix desmonta el contenido al cerrar): asi cada vez
 * arranca de la oferta elegida, o vacio, sin copiar nada en un efecto.
 */
export function DialogOferta({
  abierto,
  onCerrar,
  oferta,
  ordenNueva,
}: DialogOfertaProps): JSX.Element {
  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent tamano="lg" mobile="hoja">
        <FormularioOferta oferta={oferta} ordenNueva={ordenNueva} onCerrar={onCerrar} />
      </DialogContent>
    </Dialog>
  );
}

function FormularioOferta({
  oferta,
  ordenNueva,
  onCerrar,
}: Omit<DialogOfertaProps, "abierto">): JSX.Element {
  const crear = useCrearOferta();
  const actualizar = useActualizarOferta();
  const borrar = useBorrarOferta();
  // El catalogo entero para el buscador: son 380 y el selector filtra en el
  // cliente, que es lo que hace que escribir "15 pro" conteste al instante.
  const catalogo = useProductos({ limite: 500 });
  const productos = catalogo.data?.filas ?? [];

  // Una nueva arranca eligiendo el tipo: hasta entonces no hay formulario.
  const [datos, setDatos] = useState<OfertaEnEdicion | null>(() =>
    oferta ? desdeOferta(oferta) : null,
  );
  // El equipo elegido a mano gana; si no, el de la oferta, que se encuentra
  // cuando llega el catalogo.
  const [elegido, setElegido] = useState<Producto | null>(null);
  const producto = elegido ?? productos.find((p) => p.id === (oferta?.producto_id ?? null)) ?? null;
  const { data: config } = useConfig();
  const cotizacion = Number(config?.cotizacion_vigente ?? 0);
  /**
   * El precio que no se parece al del equipo se avisa, y el segundo envio con
   * los MISMOS valores publica. Cambiar el precio, la moneda o el equipo vuelve
   * a preguntar.
   */
  const [precioConfirmado, setPrecioConfirmado] = useState<string | null>(null);

  // LA FOTO PROPIA (0110)
  // En una que existe se sube en el momento, como la de un producto. En una
  // nueva todavia no hay id donde colgarla: queda en espera y se sube apenas
  // se crea la oferta.
  const subirFoto = useSubirImagenOferta();
  const quitarFoto = useQuitarImagenOferta();
  const [imagenUrl, setImagenUrl] = useState(oferta?.imagen_url ?? null);
  const [fotoEnEspera, setFotoEnEspera] = useState<FormData | null>(null);
  const srcEnEspera = useMemo(() => {
    const archivo = fotoEnEspera?.get("archivo");
    return archivo instanceof Blob ? URL.createObjectURL(archivo) : null;
  }, [fotoEnEspera]);
  useEffect(
    () => () => {
      if (srcEnEspera) URL.revokeObjectURL(srcEnEspera);
    },
    [srcEnEspera],
  );

  /** Cambia el tipo, el equipo o el porcentaje (ver `conElEjemplo`). */
  function conEjemplo(
    antes: OfertaEnEdicion,
    despues: OfertaEnEdicion,
    equipoAntes: Producto | null,
    equipoDespues: Producto | null,
  ): OfertaEnEdicion {
    const nombre = (p: Producto | null): string | null => (p ? nombreLegible(p.nombre) : null);
    return conElEjemplo(antes, despues, nombre(equipoAntes), nombre(equipoDespues));
  }

  if (datos === null) {
    return <ElegirTipo onElegir={(c) => setDatos(ofertaNueva(c))} onCerrar={onCerrar} />;
  }
  // Desde aca `datos` existe; el alias es para los cierres de abajo.
  const d: OfertaEnEdicion = datos;

  /** Cambia un campo que decide el ejemplo (el tipo o el porcentaje). */
  function cambiar(cambios: Partial<OfertaEnEdicion>): void {
    setDatos(conEjemplo(d, { ...d, ...cambios }, producto, producto));
  }

  /**
   * Elegir el equipo pone su nombre en el titulo del ejemplo, y el precio si
   * estaba vacio.
   *
   * El precio sale del producto y no se tipea: una oferta con un precio
   * distinto del que muestra el catalogo para el mismo telefono es una
   * discusion en el mostrador. Igual se puede pisar, porque un combo puede
   * valer otra cosa.
   */
  function elegirProducto(p: Producto): void {
    setElegido(p);
    const sinPrecio = d.precio.trim() === "";
    setDatos(
      conEjemplo(
        d,
        {
          ...d,
          titulo: d.titulo.trim() === "" ? nombreLegible(p.nombre) : d.titulo,
          precio: sinPrecio ? String(Number(p.precio_venta) || "") : d.precio,
          moneda: sinPrecio ? p.precio_moneda : d.moneda,
        },
        producto,
        p,
      ),
    );
  }

  const guardando = crear.isPending || actualizar.isPending;

  async function guardar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    // Lo que el tipo no lleva se manda vacio: si la oferta era un combo y
    // pasa a canje, el precio viejo no tiene que quedar escondido en la fila.
    const tipo = TIPOS[d.clase];
    const numero = (v: string): number | null => (v.trim() === "" ? null : Number(v));
    const precioEnvio = tipo.precio === "no" ? null : numero(d.precio);
    const clavePrecio = `${d.precio}|${d.moneda}|${producto?.id ?? ""}`;
    if (
      precioConfirmado !== clavePrecio &&
      tipo.equipo &&
      precioDesproporcionado(precioEnvio, d.moneda, producto, cotizacion)
    ) {
      setPrecioConfirmado(clavePrecio);
      return;
    }
    const envio = {
      clase: d.clase,
      titulo: d.titulo.trim(),
      detalle: d.detalle.trim() === "" ? null : d.detalle.trim(),
      precio: precioEnvio,
      moneda: d.moneda,
      precio_antes: tipo.antes ? numero(d.precio_antes) : null,
      producto_id: tipo.equipo ? (producto?.id ?? null) : null,
      porcentaje: d.clase === "EFECTIVO" ? numero(d.porcentaje) : null,
    };
    try {
      if (oferta) {
        await actualizar.mutateAsync({ id: oferta.id, cambios: envio });
        mostrarExito("Oferta guardada");
      } else {
        const nueva = await crear.mutateAsync({ ...envio, activa: true, orden: ordenNueva });
        if (fotoEnEspera) await subirFoto.mutateAsync({ id: nueva.id, formulario: fotoEnEspera });
        mostrarExito("Oferta publicada", "Ya se ve en el home.");
      }
      onCerrar();
    } catch (error) {
      mostrarError(error);
    }
  }

  async function eliminar(): Promise<void> {
    if (!oferta) return;
    if (!window.confirm(`¿Borrar la oferta «${oferta.titulo}»? No se puede deshacer.`)) return;
    try {
      await borrar.mutateAsync(oferta.id);
      mostrarExito("Oferta borrada", "Ya no se ve en el home.");
      onCerrar();
    } catch (error) {
      mostrarError(error);
    }
  }

  return (
    <form onSubmit={(e) => void guardar(e)} className="contents">
      <DialogHeader>
        <DialogTitle>{oferta ? "Editar la oferta" : "Nueva oferta"}</DialogTitle>
        <DialogDescription>
          Va arriba de todo en el home. Tocarla abre WhatsApp con el mensaje escrito.
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        {/* El tipo va PRIMERO: decide que campos hacen falta. */}
        <Campo label="Tipo de oferta">
          <SelectNativo
            value={d.clase}
            onChange={(e) => cambiar({ clase: e.target.value as Oferta["clase"] })}
          >
            {(Object.keys(TIPOS) as Oferta["clase"][]).map((c) => (
              <option key={c} value={c}>
                {TIPOS[c].nombre}
              </option>
            ))}
          </SelectNativo>
        </Campo>

        {/* Despues el equipo: elegirlo llena el titulo y el precio, y hace que
            el mensaje de WhatsApp lleve el codigo, para que quien atiende sepa
            de cual habla sin deducirlo del titulo. */}
        {TIPOS[d.clase].equipo ? (
          <>
            <SelectorProducto
              label="De qué equipo es"
              requerido={false}
              productos={productos}
              seleccionado={producto}
              onSeleccionar={elegirProducto}
            />
            <p className="-mt-2 text-xs text-muted-foreground">
              Opcional, pero conviene: completa el título y el precio, y el mensaje que te llega por
              WhatsApp trae el código del equipo.
            </p>
          </>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo label="Título" requerido className="sm:col-span-2">
            <Input
              value={d.titulo}
              onChange={(e) => setDatos({ ...d, titulo: e.target.value })}
              maxLength={90}
              required
            />
          </Campo>

          {d.clase === "EFECTIVO" ? (
            <Campo label="Descuento" requerido ayuda="Lo que se descuenta pagando en efectivo.">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="90"
                  step="1"
                  inputMode="numeric"
                  value={d.porcentaje}
                  onChange={(e) => cambiar({ porcentaje: e.target.value })}
                  required
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            </Campo>
          ) : null}

          {TIPOS[d.clase].precio !== "no" ? (
            <Campo
              label={d.clase === "EFECTIVO" ? "Precio de lista" : "Precio de la oferta"}
              requerido={TIPOS[d.clase].precio === "si"}
              ayuda={
                TIPOS[d.clase].precio === "opcional"
                  ? "Opcional. Sin precio, la tarjeta invita a consultar."
                  : undefined
              }
            >
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="1"
                  step="0.01"
                  inputMode="decimal"
                  value={d.precio}
                  onChange={(e) => setDatos({ ...d, precio: e.target.value })}
                  required={TIPOS[d.clase].precio === "si"}
                  className="flex-1"
                />
                <SelectNativo
                  aria-label="Moneda de la oferta"
                  value={d.moneda}
                  onChange={(e) => setDatos({ ...d, moneda: e.target.value as Oferta["moneda"] })}
                  className="w-28"
                >
                  <option value="ARS">Pesos</option>
                  <option value="USD">Dólares</option>
                </SelectNativo>
              </div>
              {Number(d.precio) > 0 && cotizacion > 0 ? (
                <p className="text-xs text-muted-foreground">
                  ≈{" "}
                  {formatear(
                    convertir(
                      Number(d.precio),
                      d.moneda,
                      d.moneda === "USD" ? "ARS" : "USD",
                      cotizacion,
                    ),
                    d.moneda === "USD" ? "ARS" : "USD",
                  )}{" "}
                  al dólar de hoy
                </p>
              ) : null}
              {precioConfirmado === `${d.precio}|${d.moneda}|${producto?.id ?? ""}` &&
              precioDesproporcionado(
                d.precio.trim() === "" ? null : Number(d.precio),
                d.moneda,
                producto,
                cotizacion,
              ) ? (
                <Aviso tono="warning">
                  El precio no se parece al del equipo (
                  {formatear(Number(producto?.precio_venta), producto?.precio_moneda ?? "ARS")}).
                  Revisá la moneda. Si está bien, tocá «{oferta ? "Guardar" : "Publicar la oferta"}»
                  otra vez.
                </Aviso>
              ) : null}
            </Campo>
          ) : null}

          {TIPOS[d.clase].antes ? (
            <Campo
              label="Cuánto costaba antes"
              ayuda={
                d.clase === "COMBO"
                  ? "Lo que costaría por separado. De acá sale el «Ahorrás $…»."
                  : "Se muestra tachado al lado del precio nuevo."
              }
            >
              <Input
                type="number"
                min="1"
                step="0.01"
                inputMode="decimal"
                value={d.precio_antes}
                onChange={(e) => setDatos({ ...d, precio_antes: e.target.value })}
              />
            </Campo>
          ) : null}

          <Campo
            label="Segunda línea"
            ayuda="Opcional. Va debajo del título, más chica."
            className="sm:col-span-2"
          >
            <Input
              value={d.detalle}
              onChange={(e) => setDatos({ ...d, detalle: e.target.value })}
              maxLength={90}
            />
          </Campo>

          <Campo
            label="Foto"
            ayuda="Opcional. Vertical, de 800 × 1200 px, con el equipo centrado. Sin foto se usa la del equipo."
            className="sm:col-span-2"
          >
            <BotonFoto
              src={oferta ? srcImagenOfertaAdmin(imagenUrl, oferta.id) : srcEnEspera}
              nombre="la oferta"
              tamano="grande"
              onSubir={async (formulario) => {
                if (!oferta) {
                  setFotoEnEspera(formulario);
                  return;
                }
                const o = await subirFoto.mutateAsync({ id: oferta.id, formulario });
                setImagenUrl(o.imagen_url);
              }}
              onQuitar={async () => {
                if (!oferta) {
                  setFotoEnEspera(null);
                  return;
                }
                const o = await quitarFoto.mutateAsync(oferta.id);
                setImagenUrl(o.imagen_url);
              }}
            />
          </Campo>
        </div>
      </DialogBody>

      <DialogFooter className="sm:justify-between">
        {oferta ? (
          <Button
            type="button"
            variant="ghost"
            disabled={borrar.isPending}
            onClick={() => void eliminar()}
          >
            <Trash2 aria-hidden="true" />
            Borrar
          </Button>
        ) : (
          <span />
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button type="button" variant="outline" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button type="submit" disabled={guardando}>
            {guardando ? "Guardando…" : oferta ? "Guardar" : "Publicar la oferta"}
          </Button>
        </div>
      </DialogFooter>
    </form>
  );
}

/**
 * El primer paso de una oferta nueva: que tipo es. Cada tipo muestra el
 * titulo con el que arranca, para elegir viendo el resultado y no un nombre.
 */
function ElegirTipo({
  onElegir,
  onCerrar,
}: {
  onElegir: (c: Oferta["clase"]) => void;
  onCerrar: () => void;
}): JSX.Element {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Nueva oferta</DialogTitle>
        <DialogDescription>
          Elegí el tipo. El texto viene escrito: después lo retocás.
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <ul className="divide-y divide-border rounded-md border border-border">
          {(Object.keys(TIPOS) as Oferta["clase"][]).map((c) => (
            <li key={c}>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onElegir(c)}
                className="h-auto w-full justify-start gap-3 px-3 py-3 text-left font-normal"
              >
                <Badge variant="outline" className="shrink-0">
                  {etiquetaDeOferta(c)}
                </Badge>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{TIPOS[c].nombre}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    «{ofertaNueva(c).titulo}»
                  </span>
                </span>
                <ChevronRight
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </Button>
            </li>
          ))}
        </ul>
      </DialogBody>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCerrar}>
          Cancelar
        </Button>
      </DialogFooter>
    </>
  );
}
